'use client';

import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import planSkuToggleMessages from '@/components/purchase/PlanSkuToggle/messages';
import purchaseDetailsMessages from '@/components/purchase/PurchaseDetails/messages';
import genericMessages from '@/messages/messages';

import CancelAnytimeLink from '../CancelAnytimeLink';
import { useFormatPrice, useFormatPricePlusApplicableTaxes } from '../hooks';
import { useSisu } from '../Provider';
import { isFormattedMessage, type MessageNode } from '../utils';
import messages from './messages';
import {
	Capitalize,
	Divider,
	OfferHighlight,
	OrderRowBold,
	OrderRowLight,
	OrderRowSmallLight,
	Wrapper,
} from './styles';

export type TotalDueTodayProps = {
	/**
	 * Terms to display for the total due today. Can be a formatted message or a React element.
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	terms?: MessageNode;
};

export default function TotalDueToday({ terms }: TotalDueTodayProps) {
	const { formatMessage } = useIntl();
	const formatPrice = useFormatPrice();
	const formatPricePlusApplicableTaxes = useFormatPricePlusApplicableTaxes();
	const { hasFreeTrial, selectedSku, skus } = useSisu();

	if (!selectedSku) {
		return null;
	}

	const totalDueToday = hasFreeTrial ? 0 : selectedSku.finalPrice;

	const formattedTotalDueToday = formatPrice(totalDueToday, selectedSku.currency);
	const formattedTotalDueTodayPlusApplicableTaxes = formatPricePlusApplicableTaxes(
		totalDueToday,
		selectedSku.currency,
		selectedSku.country,
	);

	const formattedBasePrice = formatPrice(selectedSku.basePrice, selectedSku.currency);
	const formattedFinalPricePlusApplicableTaxes = formatPricePlusApplicableTaxes(
		selectedSku.finalPrice,
		selectedSku.currency,
		selectedSku.country,
	);

	if (typeof terms === 'undefined') {
		if (selectedSku.trialDuration) {
			const monthly = formatPrice(selectedSku.finalPrice / 12, selectedSku.currency);
			const yearly = formattedFinalPricePlusApplicableTaxes;
			const cancelAnytimeLink = (chunk: string[]): JSX.Element => (
				<CancelAnytimeLink>{chunk}</CancelAnytimeLink>
			);
			if (selectedSku.trialDuration.days) {
				terms = formatMessage(messages.termsInDays, {
					duration: selectedSku.trialDuration.days,
					quantity: selectedSku.trialDuration.days,
					monthly,
					yearly,
					cancelAnytimeLink,
				});
			} else if (selectedSku.trialDuration.months) {
				terms = formatMessage(messages.termsInMonths, {
					duration: selectedSku.trialDuration.months,
					quantity: selectedSku.trialDuration.months,
					monthly,
					yearly,
					cancelAnytimeLink,
				});
			}
		}
	}

	return (
		<Wrapper>
			{skus.length > 1 ? null : (
				<>
					<OrderRowLight>
						<>
							<Capitalize>
								{selectedSku.duration === 'year' && formatMessage(purchaseDetailsMessages.yearlyTitle)}
								{selectedSku.duration === 'month' && formatMessage(purchaseDetailsMessages.monthlyTitle)}
							</Capitalize>
							<div>
								{selectedSku.finalPrice !== selectedSku.basePrice ? (
									<s>{formattedBasePrice}</s>
								) : (
									formattedBasePrice
								)}
								/{formatMessage(planSkuToggleMessages.yearlyAbbreviation)}
							</div>
						</>
					</OrderRowLight>
					{((typeof selectedSku.trialDuration?.days === 'number' && selectedSku.trialDuration.days > 0) ||
						(typeof selectedSku.trialDuration?.months === 'number' &&
							selectedSku.trialDuration.months > 0)) && (
						<OfferHighlight>
							<OrderRowLight>
								{typeof selectedSku.trialDuration?.days === 'number' &&
									selectedSku.trialDuration.days > 0 &&
									formatMessage(purchaseDetailsMessages.trialDays, {
										number: selectedSku.trialDuration.days,
									})}
								{typeof selectedSku.trialDuration?.months === 'number' &&
									selectedSku.trialDuration.months > 0 &&
									formatMessage(purchaseDetailsMessages.trialMonths, {
										number: selectedSku.trialDuration.months,
									})}{' '}
								{formatMessage(purchaseDetailsMessages.trial)}
								<div>{formatMessage(genericMessages.free).toUpperCase()}</div>
							</OrderRowLight>
						</OfferHighlight>
					)}
					{selectedSku.discount && (
						<OfferHighlight>
							<OrderRowLight>
								<div>
									{selectedSku.discount} {formatMessage(purchaseDetailsMessages.discount)}
								</div>
								<div>{formatPrice(selectedSku.finalPrice - selectedSku.basePrice, selectedSku.currency)}</div>
							</OrderRowLight>
						</OfferHighlight>
					)}
				</>
			)}
			<Divider />
			{skus.length > 1 ? (
				<OrderRowLight>
					<div>{formatMessage(purchaseDetailsMessages.totalDueToday)}</div>
					<div>
						{totalDueToday === 0 ? formattedTotalDueToday : formattedTotalDueTodayPlusApplicableTaxes}
					</div>
				</OrderRowLight>
			) : (
				<>
					<OrderRowBold>
						<div>
							{formatMessage(purchaseDetailsMessages.totalDueToday)}
							<span>*</span>
						</div>
						<div>
							{totalDueToday === 0 ? formattedTotalDueToday : formattedTotalDueTodayPlusApplicableTaxes}
						</div>
					</OrderRowBold>
					{totalDueToday === 0 && (
						<OrderRowLight>
							{formatMessage(purchaseDetailsMessages.totalDueAfterTrial)}
							<div>{formattedFinalPricePlusApplicableTaxes}</div>
						</OrderRowLight>
					)}
				</>
			)}
			{terms && hasFreeTrial && (
				<OrderRowSmallLight align="left" color="gray5" size={FontSizes.sm} weight={FontWeights.Medium}>
					{isFormattedMessage(terms) ? <Text size={FontSizes.sm}>{terms}</Text> : terms}
				</OrderRowSmallLight>
			)}
		</Wrapper>
	);
}
